import React, { useState, useEffect, memo, useCallback, useRef } from "react"
import {
	Box,
	Stack,
	Inline,
	Button,
	Icons,
	Container,
	Tabs,
	TabsList,
	TabItem,
	TabContent,
	Alert,
	Dropdown,
	Table,
	TableHeaderDataCell,
	TableDataCell,
} from "@sembark-travel/ui-base"
import {
	cabPriceCalculationMetricPricesSheetsXHR,
	TCabMetricSheetFileData,
} from "./store"
import { ICabType, SelectCabTypes } from "../../CabTypes"
import {
	SelectCabPriceCalculationMetric,
	SelectCabPriceCalculationMetricPreset,
	type TCabPriceCalculationMetricPreset,
	type TCabPriceCalculationMetric,
} from "../../CabPriceCalculationMetrics"
import {
	useFunctionalCurrencyOfTenant,
	SelectTenantCurrencyInputField,
} from "../../Currencies"
import {
	Form,
	TextInputField,
	withServerErrors,
	useForm,
	validateFormValues,
	FieldArray,
	arrayMutators,
	GetFieldValue,
	DatePickerField,
	SelectField,
	FormApi,
	SubmissionError,
	useFieldValue,
} from "@sembark-travel/ui-form"
import { useNavigate } from "../../router"
import { Dialog } from "@sembark-travel/ui-dialog"
import * as Validator from "yup"
import { transformMetricSheetFileDataToFormValues } from "./utils"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { SheetFormSubmissionAction, TSheetFolder } from "../store"
import { SelectFolder } from "../List"
import {
	CreateSheetFileWithInFolder,
	EditSheetFileVersionDetails,
} from "../Sheets"

export interface IAddCabMetricPrices {
	config: {
		currency: string
	}
	seasons?: Array<ICabPriceSeasons>
	cab_prices: Array<{
		id: string | undefined
		cab_type: ICabType | undefined
		cab_price_calculation_metrics: Array<ICabPriceCalculationMetrics>
	}>
	metric_sheet_folder?: TSheetFolder
	metric_sheet_file_name?: string
}

export interface ISeasonDateRange {
	start_date: Date | null
	end_date: Date | null
}

export interface ICabPriceSeasons {
	id: string
	name: string
	date_ranges: Array<ISeasonDateRange>
	is_editing: boolean
}

export interface ICabPriceCalculationMetrics {
	metric: TCabPriceCalculationMetric | undefined
	season_prices: {
		[seasonId: string]: {
			price: number | undefined
		}
	}
}

const AddPriceSpreadSheetFormValidationSchema = Validator.object().shape({
	config: Validator.object().shape({
		currency: Validator.string().required("Currency is required"),
	}),

	seasons: Validator.array()
		.of(
			Validator.object().shape({
				id: Validator.string().required(),
				name: Validator.string().required("Season name is required"),
				date_ranges: Validator.array()
					.of(
						Validator.object().shape({
							start_date: Validator.date()
								.typeError("Please select a valid start date")
								.required("Start date is required")
								.nullable(),
							end_date: Validator.date()
								.typeError("Please select a valid end date")
								.required("End date is required")
								.nullable()
								.min(
									Validator.ref("start_date"),
									"End date must be after start date"
								),
						})
					)
					.min(1, "At least one date range is required"),
				is_editing: Validator.boolean(),
			})
		)
		.min(1, "At least one season is required"),

	cab_prices: Validator.array()
		.of(
			Validator.object().shape({
				cab_type: Validator.object()
					.required("Cab type is required")
					.nullable(),
				cab_price_calculation_metrics: Validator.array()
					.of(
						Validator.object().shape({
							metric: Validator.object().required("Metric is required"),
							season_prices: Validator.lazy((obj) =>
								Validator.object(
									Object.keys(obj || {}).reduce(
										(acc, key) => ({
											...acc,
											[key]: Validator.object().shape({
												price: Validator.number()
													.typeError("Price must be a number")
													.min(0, "Price must be greater than or equal to 0"),
											}),
										}),
										{}
									)
								)
							),
						})
					)
					.min(1, "At least one calculation metric is required"),
			})
		)
		.min(1, "At least one cab price is required"),
})

export function AddPriceSpreadSheetForm({
	initialValues = undefined,
	onSubmit,
	extraFields,
	folderDetails,
}: {
	onSubmit?: (
		values: IAddCabMetricPrices,
		action: SheetFormSubmissionAction,
		isSheetUpdated?: boolean
	) => Promise<void>

	initialValues?: IAddCabMetricPrices | undefined

	extraFields?: (props: {
		form: FormApi<IAddCabMetricPrices, Partial<IAddCabMetricPrices>>
	}) => React.ReactNode
	folderDetails?: TSheetFolder
}) {
	const functionalCurrency = useFunctionalCurrencyOfTenant()
	const [initialSheetValues] = useState<IAddCabMetricPrices>(() => {
		const seasonId = `Season-${Math.ceil(Math.random() * 10000)}`
		return initialValues
			? {
					...initialValues,
					config: {
						currency:
							initialValues.config?.currency || functionalCurrency.currency,
					},
				}
			: {
					metric_sheet_folder: folderDetails,
					config: {
						currency: functionalCurrency.currency,
					},
					seasons: [
						{
							id: seasonId,
							name: "Season Name",
							date_ranges: [{ start_date: null, end_date: null }],
							is_editing: false,
						},
					],
					cab_prices: [
						{
							id: undefined,
							cab_type: undefined,
							cab_price_calculation_metrics: [
								{
									metric: undefined,
									season_prices: {
										[seasonId]: {
											price: undefined,
										},
									},
								},
							],
						},
					],
				}
	})

	const formActionRef = useRef<SheetFormSubmissionAction>(
		SheetFormSubmissionAction.SAVE_DRAFT
	)

	return (
		<Form<IAddCabMetricPrices>
			initialValues={initialSheetValues}
			validate={validateFormValues(AddPriceSpreadSheetFormValidationSchema)}
			onSubmit={withServerErrors(async (values, form) => {
				await onSubmit?.(values, formActionRef.current, form.getState().dirty)
			})}
			subscription={{ submitting: true }}
			mutators={{
				...arrayMutators,
			}}
		>
			{({ submitting, handleSubmit, form }) => {
				return (
					<form onSubmit={handleSubmit}>
						{extraFields ? <>{extraFields({ form })}</> : null}
						<Box padding={"3"}>
							<Stack overflowX="auto" position={"relative"} gap="2">
								<SelectTenantCurrencyInputField
									name="config.currency"
									style={{ maxWidth: "100px" }}
								/>

								<Box
									position={"relative"}
									overflowX="auto"
									overflowY="auto"
									zIndex="0"
									style={{
										maxHeight: "100vh",
									}}
									flexGrow={`1`}
								>
									<Table
										width="full"
										position={"relative"}
										zIndex="0"
										style={{
											borderSpacing: "0 2px",
										}}
									>
										<thead
											style={{
												position: "sticky",
												top: 0,
												zIndex: 100008,
											}}
										>
											<tr>
												<TableHeaderDataCell
													position={"sticky"}
													left={"0"}
													backgroundColor={"default"}
													boxShadow={"sm"}
													style={{
														minWidth: "100px",
														zIndex: 4,
													}}
													borderWidth={"1"}
													padding={"2"}
													fontSize="sm"
													fontWeight="semibold"
												>
													Actions
												</TableHeaderDataCell>
												<TableHeaderDataCell
													position={"sticky"}
													backgroundColor={"default"}
													boxShadow={"sm"}
													style={{
														minWidth: "200px",
														left: "100px",
														zIndex: 4,
													}}
													borderWidth={"1"}
													padding={"2"}
													fontSize="sm"
													fontWeight="semibold"
												>
													Cab Type
												</TableHeaderDataCell>
												<TableHeaderDataCell
													position={"sticky"}
													backgroundColor={"default"}
													boxShadow={"sm"}
													style={{
														minWidth: "200px",
														left: "300px",
														zIndex: 4,
													}}
													borderWidth={"1"}
													padding={"2"}
													fontSize="sm"
													fontWeight="semibold"
												>
													Calculation Metric
												</TableHeaderDataCell>
												<FieldArray name="seasons">
													{({ fields: seasonFields }) => (
														<>
															{(seasonFields || []).map(
																(seasonName, seasonIndex) => (
																	<TableHeaderDataCell
																		key={seasonName}
																		borderWidth="1"
																		padding="2"
																		style={{
																			minWidth: "350px",
																		}}
																		bgColor="default"
																	>
																		<Box>
																			<SeasonNameField
																				name={seasonName}
																				onRemove={() => {
																					if (
																						Number(seasonFields.length || 0) > 1
																					) {
																						seasonFields.remove(seasonIndex)
																					} else {
																						showSnackbar(
																							"At least one season is required."
																						)
																					}
																				}}
																			/>
																		</Box>
																	</TableHeaderDataCell>
																)
															)}
															<TableHeaderDataCell borderWidth="1" padding="2">
																<Button
																	onClick={() => {
																		seasonFields.push({
																			id: `Season-${Math.ceil(Math.random() * 10000)}`,
																			name: `Season ${Number(seasonFields.length || 0) + 1}`,
																			date_ranges: [
																				{
																					id: 1,
																					start_date: null,
																					end_date: null,
																				},
																			],
																			is_editing: false,
																		})
																	}}
																>
																	Add Season
																</Button>
															</TableHeaderDataCell>
														</>
													)}
												</FieldArray>
											</tr>

											<tr>
												<TableHeaderDataCell
													borderWidth={"1"}
													padding={"2"}
													colSpan={3}
													position={"sticky"}
													left={"0"}
													bgColor={"default"}
													boxShadow={"sm"}
													style={{
														zIndex: 4,
													}}
												></TableHeaderDataCell>

												<FieldArray<ICabPriceSeasons> name="seasons">
													{({ fields: seasonFields }) => (
														<>
															{(seasonFields || []).map(
																(seasonName, seasonIndex) => (
																	<TableHeaderDataCell
																		key={`${seasonIndex}-dates`}
																		borderWidth="1"
																		padding="2"
																		maxWidth="sm"
																		verticalAlign="top"
																		bgColor="default"
																	>
																		<GetFieldValue<ICabPriceSeasons>
																			name={`seasons[${seasonIndex}]`}
																		>
																			{({ value: season }) => (
																				<SeasonDateRanges
																					name={seasonName}
																					onAddDateRange={() => {
																						seasonFields.update(seasonIndex, {
																							...season,
																							date_ranges: [
																								...(season.date_ranges || []),
																								{
																									start_date: null,
																									end_date: null,
																								},
																							],
																						})
																					}}
																					onRemoveDateRange={(
																						dateRangeIndex
																					) => {
																						const updatedDateRanges = [
																							...(season.date_ranges || []),
																						]
																						updatedDateRanges.splice(
																							dateRangeIndex,
																							1
																						)

																						seasonFields.update(seasonIndex, {
																							...season,
																							date_ranges: updatedDateRanges,
																						})
																					}}
																				/>
																			)}
																		</GetFieldValue>
																	</TableHeaderDataCell>
																)
															)}
														</>
													)}
												</FieldArray>
											</tr>
										</thead>

										<tbody>
											<FieldArray<
												IAddCabMetricPrices["cab_prices"][number]
											> name="cab_prices">
												{({ fields: cabPriceFields }) => (
													<>
														{(cabPriceFields || []).map(
															(cabPriceName, cabPriceIndex) => (
																<CabPriceRowWrapper
																	key={`cab-price-row-wrapper-${cabPriceName}`}
																	addRow={cabPriceFields.push}
																	removeRowFromIndex={cabPriceFields.remove}
																	name={cabPriceName}
																	index={cabPriceIndex}
																/>
															)
														)}

														<tr
															key="add-row-button"
															style={{
																position: "sticky",
																left: "200px",
																zIndex: -Number(cabPriceFields.length) * 100,
															}}
														>
															<TableDataCell colSpan={3} padding={"2"}>
																<Button
																	onClick={() => {
																		const seasons =
																			form.getState().values.seasons
																		const initialSeasonPrices = (
																			seasons || []
																		).reduce(
																			(acc, season) => ({
																				...acc,
																				[season.id]: {
																					price: undefined,
																				},
																			}),
																			{}
																		)

																		cabPriceFields.push({
																			id: undefined,
																			cab_type: undefined,
																			cab_price_calculation_metrics: [
																				{
																					metric: undefined,
																					season_prices: initialSeasonPrices,
																				},
																			],
																		})
																	}}
																>
																	Add Row
																</Button>
															</TableDataCell>
														</tr>
													</>
												)}
											</FieldArray>
										</tbody>
									</Table>
								</Box>
							</Stack>

							<Stack paddingY="4" gap="2">
								<SubmissionError />
								<Inline gap="2">
									<Button
										type="submit"
										level="primary"
										disabled={submitting}
										onClick={() =>
											(formActionRef.current = SheetFormSubmissionAction.UPLOAD)
										}
									>
										{submitting &&
										formActionRef.current === SheetFormSubmissionAction.UPLOAD
											? "Uploading..."
											: "Upload Prices"}
									</Button>
									<Button
										type="submit"
										level="secondary"
										disabled={submitting}
										onClick={() =>
											(formActionRef.current =
												SheetFormSubmissionAction.SAVE_DRAFT)
										}
									>
										{submitting &&
										formActionRef.current ===
											SheetFormSubmissionAction.SAVE_DRAFT
											? "Saving..."
											: "Save Draft"}
									</Button>
								</Inline>
							</Stack>
						</Box>
					</form>
				)
			}}
		</Form>
	)
}

function SeasonNameField({
	name,
	onRemove,
}: {
	name: string
	onRemove: () => void
}) {
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
	const { value: isEditing, onChange } = useFieldValue<boolean>(
		`${name}.is_editing`
	)
	return (
		<>
			{isEditing ? (
				<Inline gap="2" alignItems="center">
					<TextInputField name={`${name}.name`} placeholder="Peak Season" />
					<Button size="sm" onClick={() => onChange(false)}>
						<Icons.Ok />
					</Button>
				</Inline>
			) : (
				<Inline gap="2" alignItems="center">
					<GetFieldValue<string> name={`${name}.name`}>
						{({ value: seasonName }) => {
							return (
								<Button
									inline
									size="sm"
									onClick={() => {
										onChange(true)
									}}
								>
									{seasonName}
								</Button>
							)
						}}
					</GetFieldValue>
					<Dropdown>
						<Dropdown.Toggle paddingX="2">
							<Button size="sm" onClick={() => onChange(false)}>
								<Icons.DotsVertical />
							</Button>
						</Dropdown.Toggle>
						<Dropdown.Menu>
							<Dropdown.MenuItem onClick={() => onChange(true)}>
								<Inline gap="2" alignItems={"center"}>
									<Icons.Pencil />
									Edit Season
								</Inline>
							</Dropdown.MenuItem>

							<Dropdown.MenuItemDivider />

							<Dropdown.MenuItem
								color="warning"
								onClick={() => setIsDeleteDialogOpen(true)}
							>
								<Inline gap="2" alignItems={"center"}>
									<Icons.Trash color="warning" />
									Delete Season
								</Inline>
							</Dropdown.MenuItem>
						</Dropdown.Menu>
					</Dropdown>
				</Inline>
			)}

			<GetFieldValue<string> name={`${name}.name`}>
				{({ value: seasonName }) => (
					<DeleteSeasonWarningDialog
						isOpen={isDeleteDialogOpen}
						onClose={() => setIsDeleteDialogOpen(false)}
						onConfirm={onRemove}
						seasonName={seasonName || ""}
					/>
				)}
			</GetFieldValue>
		</>
	)
}

const SeasonDateRanges = ({
	name,
	onAddDateRange,
	onRemoveDateRange,
}: {
	name: string
	onAddDateRange: () => void
	onRemoveDateRange: (index: number) => void
}) => {
	const { value: date_ranges } = useFieldValue<Array<ISeasonDateRange>>(
		`${name}.date_ranges`
	)
	return (
		<Stack gap="2" justifyContent="start">
			{(date_ranges || []).map((range, index) => (
				<Box key={index} paddingBottom="3">
					<Inline gap="2" alignItems="center">
						<Inline gap="2" alignItems="center">
							<DatePickerField
								name={`${name}.date_ranges[${index}].start_date`}
								required
								dateFormat="DD MMM YYYY"
							/>
							{"-"}
							<DatePickerField
								name={`${name}.date_ranges[${index}].end_date`}
								required
								dateFormat="DD MMM YYYY"
								rightAlign={true}
							/>
						</Inline>
						<Inline gap="1">
							{index > 0 ? (
								<Button
									size="sm"
									color="ghost"
									onClick={() => {
										onRemoveDateRange(index)
									}}
								>
									<Icons.Trash />
								</Button>
							) : null}
						</Inline>

						{index === Number(date_ranges.length) - 1 ? (
							<Button size="sm" onClick={onAddDateRange}>
								<Icons.Plus />
							</Button>
						) : null}
					</Inline>
				</Box>
			))}
		</Stack>
	)
}

const CabPriceRow = memo(
	({
		name,
		index,
		onDuplicate,
		onRemove,
	}: {
		name: string
		index: number
		onDuplicate?: () => void
		onRemove?: () => void
	}) => {
		const [isMetricsModalOpen, setIsMetricsModalOpen] = useState<boolean>(false)
		const form = useForm()

		return (
			<>
				<FieldArray<ICabPriceCalculationMetrics>
					name={`${name}.cab_price_calculation_metrics`}
				>
					{({ fields: metricFields }) => (
						<>
							{(metricFields || []).map((metricName, metricIndex) => {
								// Ensure upper rows stack above lower ones so dropdowns aren't hidden
								const zIndex = 100000 - index * 50 - metricIndex
								return (
									<tr key={`metric-${metricName}`}>
										<TableDataCell
											left={"0"}
											backgroundColor={"default"}
											position={"sticky"}
											style={{
												minWidth: "100px",
												zIndex: zIndex,
											}}
											padding={"2"}
										>
											<Inline gap="2">
												{metricIndex === 0 && onDuplicate ? (
													<Button
														title="Duplicate Row"
														size="sm"
														onClick={() => {
															onDuplicate()
														}}
													>
														<Icons.Duplicate />
													</Button>
												) : null}

												{metricIndex === 0 && onRemove ? (
													<Button
														size="sm"
														onClick={() => {
															onRemove()
														}}
														title="Remove Row"
													>
														<Icons.Trash />
													</Button>
												) : null}
											</Inline>
										</TableDataCell>
										<TableDataCell
											position={"sticky"}
											backgroundColor={"default"}
											style={{
												minWidth: "200px",
												left: "100px",
												zIndex: zIndex,
											}}
											padding={"2"}
										>
											{metricIndex === 0 ? (
												<SelectField
													name={`${name}.cab_type`}
													select={SelectCabTypes}
													required
													fetchOnMount
													creatable
													fullWidth
													autoComplete="off"
												/>
											) : null}
										</TableDataCell>

										<TableDataCell
											position={"sticky"}
											backgroundColor={"default"}
											style={{
												minWidth: "200px",
												left: "300px",
												zIndex: zIndex,
											}}
											padding={"2"}
											borderRightWidth={"1"}
										>
											<Inline gap={"1"} alignItems={"start"}>
												<SelectField
													name={`${metricName}.metric`}
													select={SelectCabPriceCalculationMetric}
													required
													creatable
													fullWidth
													autoComplete="off"
												/>

												<Inline gap={"1"}>
													{metricIndex === 0 ? (
														<Button
															title="Add Preset"
															size="sm"
															onClick={() => {
																setIsMetricsModalOpen(true)
															}}
														>
															<Icons.ViewList />
														</Button>
													) : null}

													{metricIndex === Number(metricFields.length) - 1 ? (
														<Button
															title="Add Cab Type"
															size="sm"
															onClick={() =>
																metricFields.push({
																	metric: undefined,
																	season_prices: {},
																})
															}
														>
															<Icons.Plus />
														</Button>
													) : null}

													{metricIndex !== 0 ? (
														<Button
															title="Remove Cab Type"
															size="sm"
															onClick={() => {
																metricFields.remove(metricIndex)
															}}
														>
															<Icons.Trash />
														</Button>
													) : null}
												</Inline>
											</Inline>
										</TableDataCell>

										<FieldArray<ICabPriceSeasons> name="seasons">
											{({ fields: seasonFields }) => (
												<>
													{(seasonFields || []).map(
														(seasonName, seasonIndex) => (
															<GetFieldValue<ICabPriceSeasons>
																key={`season-price-${seasonName}`}
																name={`seasons[${seasonIndex}]`}
															>
																{({ value: season }) => {
																	const seasonzIndex = zIndex - 1
																	return (
																		<TableDataCell
																			key={season.id}
																			position={"relative"}
																			style={{
																				zIndex: seasonzIndex,
																			}}
																			padding={"2"}
																		>
																			<TextInputField
																				name={`${metricName}.season_prices.${season.id}.price`}
																				placeholder="eg. 1000"
																				type="number"
																				step="0.01"
																				min={0}
																			/>
																		</TableDataCell>
																	)
																}}
															</GetFieldValue>
														)
													)}
												</>
											)}
										</FieldArray>
									</tr>
								)
							})}
						</>
					)}
				</FieldArray>
				<GetFieldValue<Array<ICabPriceCalculationMetrics>>
					name={`${name}.cab_price_calculation_metrics`}
				>
					{({ value: metrics, onChange }) => (
						<CabPriceCalculationMetricsDialog
							isOpen={isMetricsModalOpen}
							onClose={() => setIsMetricsModalOpen(false)}
							onSubmit={(newMetrics) => {
								const transformedMetrics: Array<ICabPriceCalculationMetrics> = (
									newMetrics || []
								).map((metric) => ({
									metric: metric.metric,
									season_prices: metric.season_prices || {},
								}))

								onChange(transformedMetrics)
							}}
							initialMetrics={metrics}
							seasons={form.getState().values.seasons}
						/>
					)}
				</GetFieldValue>
			</>
		)
	}
)

const CabPriceRowWrapper = memo(
	({
		name,
		index,
		addRow,
		removeRowFromIndex,
	}: {
		name: string
		index: number
		addRow: (item: IAddCabMetricPrices["cab_prices"][number]) => void
		removeRowFromIndex: (index: number) => void
	}) => {
		const form = useForm()
		const onDuplicate = useCallback(() => {
			const currentRow = form.getState().values.cab_prices[index]
			if (currentRow) {
				const newRow = {
					...currentRow,
					id: `cab_price-${Math.ceil(Math.random() * 10000)}`,
				}
				addRow(newRow)
			}
		}, [index, form, addRow])

		const onRemove = useCallback(() => {
			removeRowFromIndex(index)
		}, [index, removeRowFromIndex])

		return (
			<CabPriceRow
				key={`cab-price-row-${name}`}
				name={name}
				index={index}
				onDuplicate={onDuplicate}
				onRemove={onRemove}
			/>
		)
	}
)

interface AddMetricsWithPreset {
	metrics: Array<ICabPriceCalculationMetrics>
	metric_presets: TCabPriceCalculationMetricPreset | undefined
	active_tab: "preset" | "separate"
}

const CabPriceCalculationMetricsDialog = memo(
	({
		isOpen,
		onClose,
		onSubmit,
		initialMetrics = [],
		seasons,
	}: {
		isOpen: boolean
		onClose: () => void
		onSubmit: (metrics: Array<ICabPriceCalculationMetrics>) => void
		initialMetrics?: Array<ICabPriceCalculationMetrics>
		seasons: Array<ICabPriceSeasons>
	}) => {
		const [initialValues, setInitialValues] = useState<AddMetricsWithPreset>({
			metrics: initialMetrics || [],
			metric_presets: undefined,
			active_tab: "preset",
		})

		useEffect(() => {
			setInitialValues((prev) => ({
				...prev,
				metrics: initialMetrics,
			}))
		}, [initialMetrics])
		return (
			<Dialog open={isOpen} onClose={onClose} title="Add Calculation Metrics">
				<Dialog.Body>
					<Form
						initialValues={initialValues}
						onSubmit={withServerErrors(async (values) => {
							if (values.metrics?.length === 0) {
								throw new Error(
									"The preset you have selected has no metrics. Select another preset or add separate metrics."
								)
							}
							onSubmit(values.metrics)
							onClose()
						})}
						subscription={{ submitting: true }}
						mutators={{ ...arrayMutators }}
					>
						{({ handleSubmit, submitting, form }) => (
							<form onSubmit={handleSubmit} autoComplete="off">
								<GetFieldValue<
									AddMetricsWithPreset["active_tab"]
								> name="active_tab">
									{({ value: activeTab, onChange: setActiveTab }) => (
										<Tabs defaultActive={activeTab} gap="4" layout="col">
											<TabsList>
												<TabItem
													active={activeTab === "preset"}
													onClick={() => setActiveTab("preset")}
													id="preset"
												>
													Preset Metric
												</TabItem>
												<TabItem
													active={activeTab === "separate"}
													onClick={() => setActiveTab("separate")}
													id="separate"
												>
													Separate Metrics
												</TabItem>
											</TabsList>

											{activeTab === "preset" ? (
												<TabContent>
													<Stack gap="4">
														<SelectField
															select={SelectCabPriceCalculationMetricPreset}
															creatable
															name="metric_presets"
															label="Select Preset"
															required
															fetchOnMount
															fullWidth
															onChange={(
																preset: TCabPriceCalculationMetricPreset,
																name
															) => {
																form.change(name, preset)

																if (preset.metrics) {
																	form.change(
																		"metrics",
																		(preset.metrics || []).map((metric) => ({
																			metric: metric,
																			season_prices: seasons.reduce(
																				(acc, season) => ({
																					...acc,
																					[season.id]: { price: undefined },
																				}),
																				{}
																			),
																		}))
																	)
																}
															}}
															autoComplete="off"
														/>

														<GetFieldValue<TCabPriceCalculationMetricPreset> name="metric_presets">
															{({ value: selectedPreset }) =>
																selectedPreset ? (
																	<FieldArray<ICabPriceCalculationMetrics> name="metrics">
																		{({ fields: cabMetricsFields }) => (
																			<Stack gap="4">
																				{(cabMetricsFields || []).map(
																					(cabMetric, cabMetricIndex) => (
																						<SelectField
																							key={`${cabMetric}.metric`}
																							name={`${cabMetric}.metric`}
																							label="Calculation Metric"
																							select={
																								SelectCabPriceCalculationMetric
																							}
																							required
																							fullWidth
																							autoComplete="off"
																							readOnly
																						/>
																					)
																				)}
																			</Stack>
																		)}
																	</FieldArray>
																) : null
															}
														</GetFieldValue>
													</Stack>
												</TabContent>
											) : null}

											{activeTab === "separate" ? (
												<TabContent>
													<FieldArray<ICabPriceCalculationMetrics> name="metrics">
														{({ fields: cabMetricsFields }) => (
															<Stack gap="4">
																{(cabMetricsFields || []).map(
																	(cabMetric, cabMetricIndex) => (
																		<Inline
																			gap={"2"}
																			key={cabMetricIndex}
																			alignItems="start"
																		>
																			<SelectField
																				name={`${cabMetric}.metric`}
																				label="Calculation Metric"
																				select={SelectCabPriceCalculationMetric}
																				required
																				creatable
																				fullWidth
																				autoComplete="off"
																			/>
																			<Box paddingTop={"6"}>
																				<Button
																					size="base"
																					onClick={() =>
																						cabMetricsFields.remove(
																							cabMetricIndex
																						)
																					}
																					disabled={
																						Number(
																							cabMetricsFields.length || 0
																						) <= 1
																					}
																				>
																					<Icons.Trash />
																				</Button>
																			</Box>
																		</Inline>
																	)
																)}
																<Button
																	onClick={() =>
																		cabMetricsFields.push({
																			metric: undefined,
																			season_prices: seasons.reduce(
																				(acc, season) => ({
																					...acc,
																					[season.id]: { price: undefined },
																				}),
																				{}
																			),
																		})
																	}
																>
																	Add Metric
																</Button>
															</Stack>
														)}
													</FieldArray>
												</TabContent>
											) : null}
										</Tabs>
									)}
								</GetFieldValue>

								<Dialog.Footer marginTop="2">
									<Stack gap="4">
										<SubmissionError />
										<Inline gap="2" justifyContent="end">
											<Button color="secondary" onClick={onClose}>
												Cancel
											</Button>
											<Button type="submit" disabled={submitting}>
												Save Metrics
											</Button>
										</Inline>
									</Stack>
								</Dialog.Footer>
							</form>
						)}
					</Form>
				</Dialog.Body>
			</Dialog>
		)
	}
)

const DeleteSeasonWarningDialog = memo(
	({
		isOpen,
		onClose,
		onConfirm,
		seasonName,
	}: {
		isOpen: boolean
		onClose: () => void
		onConfirm: () => void
		seasonName: string
	}) => {
		return (
			<Dialog open={isOpen} onClose={onClose} title="Delete Season?">
				<Dialog.Body>
					<Stack gap="4">
						<Alert status="warning">
							Are you sure you want to delete {seasonName} ? This will remove
							all prices associated with this season.
						</Alert>
						<Inline gap="2" justifyContent="end">
							<Button color="secondary" onClick={onClose}>
								Cancel
							</Button>
							<Button
								color="danger"
								onClick={() => {
									onConfirm()
									onClose()
								}}
							>
								Delete Season
							</Button>
						</Inline>
					</Stack>
				</Dialog.Body>
			</Dialog>
		)
	}
)

export function CreateCabMetricsPricesSheetWithFolder({
	folderDetails,
}: {
	folderDetails?: TSheetFolder
}) {
	const navigate = useNavigate()
	return (
		<CreateSheetFileWithInFolder<TCabMetricSheetFileData, IAddCabMetricPrices>
			service={{
				saveSheetVersionDetails: (xhr, folderId, fileName, sheetData) => {
					return cabPriceCalculationMetricPricesSheetsXHR(
						xhr
					).saveCabMetricSheetFile(sheetData, Number(folderId), fileName)
				},
				uploadSheetVersion: (xhr, versionId) => {
					return cabPriceCalculationMetricPricesSheetsXHR(
						xhr
					).uploadCabMetricSheetFile(versionId)
				},
			}}
			onSuccess={(folderId, fileId, fileVersionId) => {
				navigate(
					"/data-drive/folders/:folderId/files/:fileId/versions/:versionId/view",
					{
						params: {
							folderId: folderId,
							fileId: fileId,
							versionId: fileVersionId,
						},
					}
				)
			}}
		>
			{({ createSheetFile }) => (
				<AddPriceSpreadSheetForm
					folderDetails={folderDetails}
					onSubmit={(values, action) => {
						if (!values.metric_sheet_folder?.id) {
							throw Error("Folder name is required")
						}
						if (!values.metric_sheet_file_name) {
							throw Error("File name is required")
						}
						return createSheetFile(
							String(values.metric_sheet_folder.id),
							values.metric_sheet_file_name,
							values,
							action
						)
					}}
					extraFields={() => (
						<Container bgColor={"default"} paddingY={"8"}>
							<Stack gap="4" maxWidth={"xs"}>
								<SelectField
									select={SelectFolder}
									name="metric_sheet_folder"
									label="Enter a folder name"
									placeholder="e.g. Rajasthan Cabs"
									creatable
									required
								/>

								<TextInputField
									name="metric_sheet_file_name"
									required
									label={"Enter Sheet File Name"}
									placeholder="e.g. Jaipur City Cabs"
								/>
							</Stack>
						</Container>
					)}
				/>
			)}
		</CreateSheetFileWithInFolder>
	)
}

export function EditCabMetricsPricesSheet({
	metricFolderId,
	metricSheetId,
	metricVersionId,
}: {
	metricFolderId: string
	metricSheetId: string
	metricVersionId: string
}) {
	const navigate = useNavigate()

	return (
		<EditSheetFileVersionDetails<TCabMetricSheetFileData, IAddCabMetricPrices>
			sheetFolderId={metricFolderId}
			sheetId={metricSheetId}
			sheetVersionId={metricVersionId}
			xhrFetchBaseUrl="/data-drive/"
			service={{
				fetchSheetVersionDetails: (xhr, versionId) => {
					return cabPriceCalculationMetricPricesSheetsXHR(
						xhr
					).getCabMetricSheetFileVersionDetails(versionId)
				},
				saveSheetVersionDetails: (xhr, folderId, fileName, sheetData) => {
					return cabPriceCalculationMetricPricesSheetsXHR(
						xhr
					).saveCabMetricSheetFile(
						sheetData,
						Number(folderId),
						fileName,
						Number(metricSheetId)
					)
				},
				uploadSheetVersion: (xhr, versionId) => {
					return cabPriceCalculationMetricPricesSheetsXHR(
						xhr
					).uploadCabMetricSheetFile(versionId)
				},
			}}
			transformDataToFormValues={transformMetricSheetFileDataToFormValues}
			onDraftSuccess={(folderId, fileId, fileVersionId) => {
				navigate(
					"/data-drive/folders/:folderId/files/:fileId/versions/:versionId/view",
					{
						params: {
							folderId: folderId,
							fileId: fileId,
							versionId: fileVersionId,
						},
					}
				)
			}}
			onUploadSuccess={(folderId, fileId, fileVersionId) => {
				navigate(
					"/data-drive/folders/:folderId/files/:fileId/versions/:versionId/view",
					{
						params: {
							folderId: folderId,
							fileId: fileId,
							versionId: fileVersionId,
						},
					}
				)
			}}
		>
			{({ sheetValues, onUpdateSheetFile }) => (
				<AddPriceSpreadSheetForm
					initialValues={sheetValues}
					onSubmit={onUpdateSheetFile}
				/>
			)}
		</EditSheetFileVersionDetails>
	)
}
